import { useDropzone, type FileRejection } from 'react-dropzone';
import { Badge, Button } from '@monksflow/monks-ui';
import { UploadIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  ACCEPTED_FILE_FORMATS,
  MAX_FILE_SIZE_MB,
  SHOWCASED_FORMATS,
} from './GuidelineFormSheet.types';

export interface GuidelineImportDropzoneProps {
  files: File[];
  onDrop: (files: File[]) => void;
  onDropRejected: (rejections: FileRejection[]) => void;
  onRemoveFile: (index: number) => void;
  error: string | null;
  disabled?: boolean;
}

/**
 * Tessra-styled replacement for the shared `Dropzone`, which renders through MUI
 * and would reintroduce it transitively into this slice. `react-dropzone`'s
 * `useDropzone` hook is headless, so styling is ordinary Tailwind.
 */
export function GuidelineImportDropzone({
  files,
  onDrop,
  onDropRejected,
  onRemoveFile,
  error,
  disabled = false,
}: GuidelineImportDropzoneProps) {
  const { t } = useTranslation('knowledge');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: ACCEPTED_FILE_FORMATS,
    disabled,
    multiple: true,
  });

  return (
    <div className="flex flex-col gap-2xs">
      <div
        {...getRootProps()}
        className={`flex flex-col items-center gap-2xs rounded-lg border border-dashed p-xl text-center ${
          isDragActive ? 'border-primary bg-accent' : 'border-border bg-muted'
        } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      >
        <input {...getInputProps()} />
        <UploadIcon className="size-5 text-muted-foreground" />
        <span className="text-sm text-foreground">
          {t('guidelines.createGuidelineModal.dragAndDropFilesHere')}
        </span>
        <div className="flex flex-wrap justify-center gap-2xs">
          {SHOWCASED_FORMATS.map((format) => (
            <Badge key={format} variant="secondary" roundness="full">
              {format}
            </Badge>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">
          {t('guidelines.createGuidelineModal.maxSizeIs', { maxSizeMb: MAX_FILE_SIZE_MB })}
        </span>
      </div>

      {error && (
        <span className="text-sm text-destructive">
          {error}
          <br />
          {t('guidelines.createGuidelineModal.pleaseUploadFilesOnly', {
            formats: SHOWCASED_FORMATS.join(', '),
          })}
        </span>
      )}

      {files.length > 0 && (
        <div className="flex flex-col gap-2xs">
          <span className="text-sm font-medium text-muted-foreground">
            {t('guidelines.createGuidelineModal.uploadedFiles', { fileCount: files.length })}
          </span>
          <div className="flex flex-col gap-2xs">
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between gap-xs rounded-sm border border-border bg-muted px-xs py-2xs"
              >
                <span className="truncate text-sm text-foreground">{file.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-mini"
                  onClick={() => onRemoveFile(index)}
                  disabled={disabled}
                  aria-label={t('guidelines.guidelineFormSheet.removeFile')}
                >
                  <XIcon className="size-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

GuidelineImportDropzone.displayName = 'GuidelineImportDropzone';
